import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, removePartyMovementBySource } from "@/lib/party-ledger";
import { fiscalYearLabel } from "@/features/purchase/constants";
import {
  computeLineAmounts,
  computePoTotals,
  type ComputedLine,
} from "@/features/purchase/api";
import type { InvoiceFormValues } from "@/features/invoices/schema";
import type {
  Invoice,
  InvoiceLine,
  InvoiceLineTax,
  InvoiceListRow,
  InvoiceStatus,
} from "@/features/invoices/types";
import type { TaxRate } from "@/features/settings/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateInv(raw: Invoice): Invoice {
  return {
    ...raw,
    subtotal: money(raw.subtotal),
    discount: money(raw.discount),
    tax_total: money(raw.tax_total),
    adjustment: money(raw.adjustment),
    grand_total: money(raw.grand_total),
  };
}

function hydrateLine(raw: InvoiceLine): InvoiceLine {
  return {
    ...raw,
    qty: money(raw.qty),
    weight: money(raw.weight),
    unit_rate: money(raw.unit_rate),
    line_subtotal: money(raw.line_subtotal),
    gst_slab: money(raw.gst_slab),
    taxes: (raw.taxes ?? []).map((t) => ({
      ...t,
      rate: money(t.rate),
      amount: money(t.amount),
    })),
  };
}

async function nextInvoiceNumber(issueDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("invoice_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix =
    ((company?.invoice_prefix as string | undefined) ?? "INV").trim().toUpperCase() || "INV";
  const fy = fiscalYearLabel(issueDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("invoices")
    .select("invoice_number")
    .ilike("invoice_number", `${pattern}%`)
    .order("invoice_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.invoice_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  const next = (Number.isNaN(n) ? 0 : n) + 1;
  return `${pattern}${String(next).padStart(4, "0")}`;
}

async function loadClientPos(clientId: string) {
  const { data, error } = await supabase
    .from("clients")
    .select("place_of_supply, state, payment_terms, name, code, addr1, city, pincode")
    .eq("id", clientId)
    .single();
  if (error) throw error;
  return data as {
    place_of_supply: string | null;
    state: string;
    payment_terms: string;
    name: string;
    code: string;
    addr1: string;
    city: string;
    pincode: string;
  };
}

async function loadCompanyState() {
  const { data } = await supabase
    .from("company")
    .select("state, name, addr1, city, pincode")
    .limit(1)
    .maybeSingle();
  return data as {
    state: string | null;
    name: string | null;
    addr1: string | null;
    city: string | null;
    pincode: string | null;
  } | null;
}

async function loadTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from("tax_rates").select("*").eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...(r as TaxRate),
    rate: money((r as TaxRate).rate),
    gst_slab:
      (r as TaxRate).gst_slab === null || (r as TaxRate).gst_slab === undefined
        ? null
        : money((r as TaxRate).gst_slab),
  }));
}

async function replaceLines(
  invId: string,
  values: InvoiceFormValues,
  rates: TaxRate[],
  companyState: string | null | undefined,
  clientPos: string | null | undefined,
) {
  const { error: delErr } = await supabase.from("invoice_lines").delete().eq("invoice_id", invId);
  if (delErr) throw delErr;

  const computedLines: ComputedLine[] = [];
  for (let i = 0; i < values.lines.length; i++) {
    const line = values.lines[i];
    const computed = computeLineAmounts(
      line,
      rates,
      companyState,
      clientPos,
      values.issue_date,
    );
    computedLines.push(computed);

    const { data: inserted, error: lineErr } = await supabase
      .from("invoice_lines")
      .insert({
        invoice_id: invId,
        line_no: i + 1,
        material_id: line.material_id,
        material_grade_id: text(line.material_grade_id),
        shape_size_id: text(line.shape_size_id),
        description: text(line.description),
        qty: line.qty,
        weight: line.weight,
        uom_id: text(line.uom_id),
        unit_rate: line.unit_rate,
        line_subtotal: computed.line_subtotal,
        gst_slab: line.gst_slab,
      })
      .select("id")
      .single();
    if (lineErr) throw lineErr;

    if (computed.taxes.length) {
      const { error: taxErr } = await supabase.from("invoice_line_taxes").insert(
        computed.taxes.map((t) => ({
          line_id: inserted.id,
          tax_rate_id: t.tax_rate_id,
          tax_type: t.tax_type,
          rate: t.rate,
          amount: t.amount,
        })),
      );
      if (taxErr) throw taxErr;
    }
  }

  return computePoTotals(computedLines, values.discount, values.adjustment);
}

async function syncArInvoice(inv: Invoice) {
  if (inv.status === "issued" && inv.grand_total > 0) {
    await postPartyMovement({
      party: "client",
      partyId: inv.client_id,
      kind: "invoice",
      amount: inv.grand_total,
      movementDate: inv.issue_date,
      reference: inv.invoice_number,
      notes: inv.so_number ? `Against ${inv.so_number}` : null,
      sourceType: "invoice",
      sourceId: inv.id,
    });
  } else if (inv.status === "cancelled" || inv.status === "draft") {
    await removePartyMovementBySource({
      party: "client",
      partyId: inv.client_id,
      sourceType: "invoice",
      sourceId: inv.id,
    });
  }
}

export function invoiceToForm(inv: Invoice): InvoiceFormValues {
  return {
    client_id: inv.client_id,
    sales_order_id: inv.sales_order_id ?? "",
    client_po_ref: inv.client_po_ref ?? "",
    issue_date: inv.issue_date,
    due_date: inv.due_date ?? "",
    payment_terms: inv.payment_terms ?? "",
    bill_to_text: inv.bill_to_text ?? "",
    ship_to_text: inv.ship_to_text ?? "",
    discount: inv.discount,
    adjustment: inv.adjustment,
    notes: inv.notes ?? "",
    terms: inv.terms ?? "",
    lines: (inv.lines ?? []).map((line) => ({
      id: line.id,
      material_id: line.material_id,
      material_grade_id: line.material_grade_id ?? "",
      shape_size_id: line.shape_size_id ?? "",
      description: line.description ?? "",
      qty: line.qty,
      weight: line.weight,
      uom_id: line.uom_id ?? "",
      unit_rate: line.unit_rate,
      gst_slab: line.gst_slab,
    })),
  };
}

export function useInvoices(filters?: { clientId?: string }) {
  return useQuery({
    queryKey: ["invoices", filters?.clientId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<InvoiceListRow[]> => {
      let q = supabase
        .from("invoices")
        .select("*, clients(code, name), sales_orders(so_number), invoice_lines(id)")
        .order("issue_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const client = raw.clients as { code?: string; name?: string } | null;
        const so = raw.sales_orders as { so_number?: string } | null;
        const lines = raw.invoice_lines as { id: string }[] | null;
        const { clients: _c, sales_orders: _s, invoice_lines: _l, ...rest } = raw;
        return {
          ...hydrateInv(rest as unknown as Invoice),
          client_code: client?.code ?? null,
          client_name: client?.name ?? null,
          so_number: so?.so_number ?? null,
          line_count: lines?.length ?? 0,
        };
      });
    },
  });
}

export function useInvoice(id?: string) {
  return useQuery({
    queryKey: ["invoices", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<Invoice> => {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `*,
          clients(code, name, place_of_supply, state),
          sales_orders(so_number),
          invoice_lines(
            *,
            invoice_line_taxes(*),
            materials(name),
            material_grades(name),
            shape_sizes(size_label),
            uoms(code)
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const client = raw.clients as { code?: string; name?: string } | null;
      const so = raw.sales_orders as { so_number?: string } | null;
      const linesRaw = (raw.invoice_lines as Record<string, unknown>[] | null) ?? [];
      const { clients: _c, sales_orders: _s, invoice_lines: _l, ...rest } = raw;

      const lines = linesRaw
        .map((line) => {
          const taxes = ((line.invoice_line_taxes as InvoiceLineTax[] | null) ?? []).map((t) => ({
            ...t,
            rate: money(t.rate),
            amount: money(t.amount),
          }));
          const material = line.materials as { name?: string } | null;
          const grade = line.material_grades as { name?: string } | null;
          const shape = line.shape_sizes as { size_label?: string } | null;
          const uom = line.uoms as { code?: string } | null;
          const {
            invoice_line_taxes: _t,
            materials: _m,
            material_grades: _g,
            shape_sizes: _sz,
            uoms: _u,
            ...lineRest
          } = line;
          return hydrateLine({
            ...(lineRest as unknown as InvoiceLine),
            taxes,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
            uom_code: uom?.code ?? null,
          });
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...hydrateInv(rest as unknown as Invoice),
        client_code: client?.code ?? null,
        client_name: client?.name ?? null,
        so_number: so?.so_number ?? null,
        lines,
      };
    },
  });
}

async function insertInvoice(
  values: InvoiceFormValues,
  opts: { status: InvoiceStatus; userId: string | undefined },
) {
  const [client, company, rates] = await Promise.all([
    loadClientPos(values.client_id),
    loadCompanyState(),
    loadTaxRates(),
  ]);
  const invNumber = await nextInvoiceNumber(values.issue_date);
  const billTo =
    text(values.bill_to_text) ??
    [client.name, client.addr1, client.city, client.pincode].filter(Boolean).join("\n");

  const { data: header, error } = await supabase
    .from("invoices")
    .insert({
      invoice_number: invNumber,
      client_id: values.client_id,
      sales_order_id: text(values.sales_order_id),
      client_po_ref: text(values.client_po_ref),
      issue_date: values.issue_date,
      due_date: text(values.due_date),
      status: opts.status,
      bill_to_text: billTo,
      ship_to_text: text(values.ship_to_text),
      payment_terms: text(values.payment_terms) ?? client.payment_terms,
      notes: text(values.notes),
      terms: text(values.terms),
      discount: values.discount,
      adjustment: values.adjustment,
      created_by: opts.userId ?? null,
      updated_by: opts.userId ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;

  const clientPos = client.place_of_supply || client.state;
  const totals = await replaceLines(header.id, values, rates, company?.state, clientPos);

  const { data: updated, error: updErr } = await supabase
    .from("invoices")
    .update({ ...totals, updated_at: new Date().toISOString() })
    .eq("id", header.id)
    .select("*")
    .single();
  if (updErr) throw updErr;

  const inv = hydrateInv(updated as Invoice);
  if (opts.status === "issued") {
    let soNumber: string | null = null;
    if (inv.sales_order_id) {
      const { data: so } = await supabase
        .from("sales_orders")
        .select("so_number")
        .eq("id", inv.sales_order_id)
        .maybeSingle();
      soNumber = (so?.so_number as string | undefined) ?? null;
    }
    await syncArInvoice({ ...inv, so_number: soNumber });
  }
  return inv;
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      values,
      issue = false,
    }: {
      values: InvoiceFormValues;
      issue?: boolean;
    }) => insertInvoice(values, { status: issue ? "issued" : "draft", userId: user?.id }),
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.issue ? "Invoice issued" : "Invoice draft saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create invoice"),
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
      replaceLines: doReplace,
      issue = false,
    }: {
      id: string;
      values: InvoiceFormValues;
      replaceLines: boolean;
      issue?: boolean;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const inv = hydrateInv(existing as Invoice);
      if (inv.status !== "draft") throw new Error("Only draft invoices can be edited");

      const [client, company, rates] = await Promise.all([
        loadClientPos(values.client_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);

      const patch: Record<string, unknown> = {
        client_id: values.client_id,
        sales_order_id: text(values.sales_order_id),
        client_po_ref: text(values.client_po_ref),
        issue_date: values.issue_date,
        due_date: text(values.due_date),
        bill_to_text: text(values.bill_to_text),
        ship_to_text: text(values.ship_to_text),
        payment_terms: text(values.payment_terms) ?? client.payment_terms,
        notes: text(values.notes),
        terms: text(values.terms),
        discount: values.discount,
        adjustment: values.adjustment,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (issue) patch.status = "issued";

      if (doReplace) {
        const clientPos = client.place_of_supply || client.state;
        const totals = await replaceLines(id, values, rates, company?.state, clientPos);
        Object.assign(patch, totals);
      }

      const { data: updated, error } = await supabase
        .from("invoices")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;

      const result = hydrateInv(updated as Invoice);
      if (issue) {
        let soNumber: string | null = null;
        if (result.sales_order_id) {
          const { data: so } = await supabase
            .from("sales_orders")
            .select("so_number")
            .eq("id", result.sales_order_id)
            .maybeSingle();
          soNumber = (so?.so_number as string | undefined) ?? null;
        }
        await syncArInvoice({ ...result, so_number: soNumber });
      }
      return result;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["invoices", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.issue ? "Invoice issued" : "Invoice saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save invoice"),
  });
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: InvoiceStatus }) => {
      const { data: existing, error: exErr } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const inv = hydrateInv(existing as Invoice);

      if (status === "issued" && inv.status !== "draft") {
        throw new Error("Only drafts can be issued");
      }
      if (status === "cancelled" && inv.status === "cancelled") {
        throw new Error("Already cancelled");
      }

      const { data, error } = await supabase
        .from("invoices")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;

      const updated = hydrateInv(data as Invoice);
      let soNumber: string | null = null;
      if (updated.sales_order_id) {
        const { data: so } = await supabase
          .from("sales_orders")
          .select("so_number")
          .eq("id", updated.sales_order_id)
          .maybeSingle();
        soNumber = (so?.so_number as string | undefined) ?? null;
      }
      await syncArInvoice({ ...updated, so_number: soNumber });
      return updated;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["invoices", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error: fetchErr } = await supabase
        .from("invoices")
        .select("status")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;
      if ((data as { status: InvoiceStatus }).status !== "draft") {
        throw new Error("Only draft invoices can be deleted");
      }
      const { error } = await supabase.from("invoices").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Invoice deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete invoice"),
  });
}

export function useCreateInvoiceFromSalesOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (salesOrderId: string) => {
      const { data: so, error } = await supabase
        .from("sales_orders")
        .select(
          `*,
          sales_order_lines(*)`,
        )
        .eq("id", salesOrderId)
        .single();
      if (error) throw error;

      const raw = so as Record<string, unknown>;
      if (["draft", "cancelled"].includes(String(raw.status))) {
        throw new Error("Confirm the sales order before invoicing");
      }

      const linesRaw = (raw.sales_order_lines as Record<string, unknown>[] | null) ?? [];
      const values: InvoiceFormValues = {
        client_id: raw.client_id as string,
        sales_order_id: salesOrderId,
        client_po_ref: (raw.client_po_ref as string | null) ?? "",
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: (raw.due_date as string | null) ?? "",
        payment_terms: (raw.payment_terms as string | null) ?? "",
        bill_to_text: (raw.bill_to_text as string | null) ?? "",
        ship_to_text: (raw.ship_to_text as string | null) ?? "",
        discount: money(raw.discount),
        adjustment: money(raw.adjustment),
        notes: (raw.notes as string | null) ?? "",
        terms: (raw.terms as string | null) ?? "",
        lines: linesRaw
          .sort((a, b) => (a.line_no as number) - (b.line_no as number))
          .map((line) => ({
            material_id: line.material_id as string,
            material_grade_id: (line.material_grade_id as string | null) ?? "",
            shape_size_id: (line.shape_size_id as string | null) ?? "",
            description: (line.description as string | null) ?? "",
            qty: money(line.qty),
            weight: money(line.weight),
            uom_id: (line.uom_id as string | null) ?? "",
            unit_rate: money(line.unit_rate),
            gst_slab: money(line.gst_slab),
          })),
      };

      if (!values.lines.length) throw new Error("Sales order has no lines");
      return insertInvoice(values, { status: "draft", userId: user?.id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Invoice draft created from sales order");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create invoice"),
  });
}
